// ExperienceCard.js

import { Card, CardBody, Heading, Stack, Image, Text, Link, Box, Flex } from '@chakra-ui/react';
import { ExternalLink } from 'lucide-react';
import { motion, useAnimation } from 'framer-motion';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

interface ExperienceCardProps {
    jobTitle: string;
    companyName: string;
    startDate: string;
    endDate: string;
    description: string;
    image: string;
    projects?: { [key: string]: string };
    tags: string[];
    link: string;
    isHovered: boolean;
    setIsHovered: (value: boolean) => void;
    hoverActive: boolean;
    setIsHoverActive: (value: boolean) => void;
}

const MotionBox = motion(Box);

export default function ExperienceCard(props: ExperienceCardProps) {

    const isMobile = window.innerWidth <= 768;

    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    useEffect(() => {
        if (inView) {
            controls.start('visible');
        }
    }, [controls, inView]);

    function determineOpacity(isHovered: boolean, hoverActive: boolean) {
        if (hoverActive && !isHovered && !isMobile) {
            return 0.5;
        } else {
            return 1;
        }
    }

    return (
        <MotionBox
            ref={ref}
            initial="hidden"
            animate={controls}
            variants={{
                visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
                hidden: { opacity: 0, y: 20 }
            }}
        >
            <Link
                width={{ base: "100%", sm: "80%" }}
                href={props.link}
                isExternal
                alignItems={"center"}
                justifyContent={"center"}
                opacity={determineOpacity(props.isHovered, props.hoverActive)}
                transition={"opacity 0.15s ease-in-out"}
                onMouseEnter={() => {
                    props.setIsHovered(true);
                    props.setIsHoverActive(true);
                }}
                onMouseLeave={() => {
                    props.setIsHovered(false);
                    props.setIsHoverActive(false);
                }}
                _hover={{
                    textDecoration: "none",
                    cursor: "default",
                }}
            >
                <Card
                    direction={{ base: 'column', sm: 'row' }}
                    backgroundColor={"black.300"}
                    transition={"background-color 0.15s ease-in-out"}
                >
                    <Image
                        width={{ base: '65%', sm: '25%' }}
                        height={{ base: 'auto', sm: '100%' }}
                        margin={"auto"}
                        padding={5}
                        src={props.image}
                        alt='Company logo'
                    />

                    <Stack>
                        <CardBody>
                            <Heading
                                fontSize={"xl"}
                                fontFamily={"Source Code Pro Variable"}
                                fontWeight="600"
                                textAlign="left"
                                color={!isMobile && props.isHovered ? "blue.300" : "white.200"}
                                transform={!isMobile && props.isHovered ? "skewX(-10deg)" : "none"}
                                transition={"transform 0.1s ease-in-out"}
                            >
                                {props.jobTitle} · {props.companyName}
                            </Heading>
                            <Text
                                fontSize={"sm"}
                                fontFamily={"Source Code Pro Variable"}
                                fontWeight="400"
                                textAlign="left"
                                color={"white.300"}
                                marginBottom={"2%"}
                            >
                                {props.startDate} - {props.endDate}
                            </Text>
                            <Text
                                fontSize={"sm"}
                                fontFamily={"Source Code Pro Variable"}
                                fontWeight="400"
                                textAlign="left"
                                color={"white.300"}
                            >
                                {props.description}
                            </Text>
                            {props.projects && Object.keys(props.projects).length > 0 ? (
                                Object.keys(props.projects).map((project, index) => (
                                    <Link
                                        key={index}
                                        href={props.projects?.[project] ?? ''}
                                        isExternal
                                    >
                                        <Flex
                                            alignItems={"center"}
                                            borderRadius={"md"}
                                            width={"min-content"}
                                            gap={2}
                                        >
                                            <Text
                                                fontSize={"sm"}
                                                fontFamily={"Source Code Pro Variable"}
                                                fontWeight="400"
                                                textAlign="left"
                                                color={"blue.200"}
                                                marginTop={2}
                                                marginBottom={2}
                                            >
                                                {project}
                                            </Text>
                                            <ExternalLink
                                                size={16}
                                                color='#03A9F4'
                                            />
                                        </Flex>
                                    </Link>
                                ))
                            ) : (
                                <Box height={"2rem"} />
                            )}
                            <Flex
                                wrap={"wrap"}
                                width={"100%"}
                                gap={2}
                                marginTop={2}
                            >
                                {props.tags.map((tag, index) => (
                                    <Box
                                        key={index}
                                        height={"min-content"}
                                        paddingLeft={2}
                                        paddingRight={2}
                                        backgroundColor={"blue.400"}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                        borderRadius={"full"}
                                    >
                                        <Text
                                            fontSize={"sm"}
                                            fontFamily={"Source Code Pro Variable"}
                                            fontWeight="400"
                                            textAlign="center"
                                            color={"blue.300"}
                                            padding={1}
                                        >
                                            {tag}
                                        </Text>
                                    </Box>
                                ))}
                            </Flex>
                        </CardBody>
                    </Stack>
                </Card>
            </Link>
        </MotionBox>
    );
};
